import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useUserDetails from '../hooks/useUserDetails';
import useErrorModal from '../hooks/useErrorModal';
import CustomButton from '../reusable/CustomButton';
import Loader from '../reusable/Loader';
import CreateSurveySection from './CreateSurveySection';
import SurveysList from './SurveysList';
import { getSurveys, SurveyListItem } from '../../services/surveyService';
import GoBackIcon from '../assets/transcript-selection-arrow-left.svg';
import './SurveyManagement.css';

const SurveyManagement = () => {
  const navigate = useNavigate();
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();

  const [surveys, setSurveys] = useState<SurveyListItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSurveys = useCallback(async () => {
    if (!userDetails) return;

    try {
      setLoading(true);
      const surveysData = await getSurveys(userDetails.eoaId, userDetails.access_token);
      setSurveys(surveysData);
    } catch (error: any) {
      console.error('Error fetching surveys:', error);
      const errorMessage = error?.response?.data?.detail || 'Failed to fetch surveys. Please try again.';
      handleOpenErrorModal(errorMessage, error as Error);
    } finally {
      setLoading(false);
    }
  }, [userDetails, handleOpenErrorModal]);

  useEffect(() => {
    fetchSurveys();
  }, [fetchSurveys]);

  const handleSurveyQuestionDeleted = (surveyId: string) => {
    setSurveys((prev) =>
      prev.map((survey) =>
        survey.id === surveyId ? { ...survey, question_count: Math.max(0, survey.question_count - 1) } : survey
      )
    );
  };

  if (!userDetails) return <Loader />;

  if (loading) return <Loader />;

  return (
    <div className="main-content">
      <CustomButton
        variant="text"
        buttonText="Back"
        icon={<img src={GoBackIcon} alt="go back" />}
        iconPosition="start"
        buttonAction={() => navigate('/overview')}
      />
      <div className="survey-management-header">
        <h1 className="survey-management-page-title">Survey Management</h1>
        <p className="survey-management-description">Create and manage surveys for your EOA</p>
      </div>
      <CreateSurveySection />
      <SurveysList surveys={surveys} onSurveyQuestionDeleted={handleSurveyQuestionDeleted} />
    </div>
  );
};

export default SurveyManagement;
